import axios from "axios";
import { useEffect, useState } from "react";
import {
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import Table from "../../components/Table";
import { ENDPOINT } from "../../constants/endpointConstants";
import { SUPPLY_CHECKLIST } from "../../constants/sharedConstans";
import { getToken, getUserData } from "../../utils/utils";
import CloseCashierCard from "./components/CloseCashierCard";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("es-CL").format(value);
};

const ClosedCashier = () => {
  const [itemsClosedCashier, setItemsClosedCashier] = useState([]);
  const [cashierData, setCashierData] = useState([]);
  const [closeSales, setCloseSales] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSales, setIsLoadingSales] = useState(false);

  const [showSupplyModal, setShowSupplyModal] = useState(false);
  const [selectedSupplies, setSelectedSupplies] = useState({});
  const [sendingReport, setSendingReport] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [cashierSalesCount, setCashierSalesCount] = useState(0);
  const [cashierTotalAmount, setCashierTotalAmount] = useState(0);
  const [comment, setComment] = useState("");
  const [cashierTotalDeliveryAmount, setCashierTotalDeliveryAmount] =
    useState(0);
  const [cashierPaymentMethods, setCashierPaymentMethods] = useState([]);

  const [cancelSaleModal, setCancelSaleModal] = useState(false);
  const [cancelSaleId, setCancelSaleId] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelErrorMsg, setCancelErrorMsg] = useState("");

  useEffect(() => {
    getPendingSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPendingSales = async () => {
    try {
      const token = getToken();
      setIsLoadingSales(true);
      const response = await axios.get(ENDPOINT.GET_PENDING_SALES, token);
      const { sales, paymentMethods, totalAmount, totalDeliveryAmount } =
        response.data;

      setItemsClosedCashier(sales);
      setCashierTotalAmount(totalAmount);
      setCashierTotalDeliveryAmount(totalDeliveryAmount);
      setCashierPaymentMethods(paymentMethods);

      const newCashierData = [
        {
          title: "Ventas",
          data: [
            {
              name: "Total",
              value: totalAmount,
            },
          ],
        },
        {
          title: "Metodos de Pago",
          data: paymentMethods.map((method) => ({
            name: method.paymentMethodName,
            value: method.amount,
          })),
        },
        {
          title: "Domicilios",
          data: [
            {
              name: "Total",
              value: totalDeliveryAmount,
            },
          ],
        },
        {
          title: "Domicilios por Metodo",
          data: paymentMethods.map((method) => ({
            name: method.paymentMethodName,
            value: method.deliveryAmount,
          })),
        },
      ];

      setCashierData(newCashierData);
      setIsLoadingSales(false);
    } catch (error) {
      setIsLoadingSales(false);
      console.error("Error fetching pending sales:", error);
    }
  };

  const closeSale = () => {
    setCloseSales(true);
  };

  const cancelSaleClick = (sale) => {
    setCancelSaleId(sale.saleId);
    setCancelErrorMsg("");
    setCancelSaleModal(true);
  };

  const closeCancelSale = () => {
    setCancelSaleModal(false);
    setCancelSaleId(null);
    setCancelErrorMsg("");
  };

  const confirmCancelSale = async () => {
    const token = getToken();
    try {
      setCancelLoading(true);
      await axios.post(
        ENDPOINT.POST_CANCEL_SALE,
        { saleId: cancelSaleId },
        token
      );
      setCancelLoading(false);
      closeCancelSale();
      getPendingSales();
    } catch (error) {
      setCancelLoading(false);
      setCancelErrorMsg(
        error.response?.data?.code || "Error al cancelar el pedido"
      );
    }
  };

  const cancelButtonContent = () => {
    if (cancelLoading) {
      return (
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
      );
    }
    return "Si";
  };

  const closeModalSale = () => {
    setCloseSales(false);
  };

  const isAdmin = getUserData()?.role === 1;

  const closeCashier = async () => {
    try {
      const token = getToken();
      setIsLoading(true);
      const { userId, name } = getUserData();
      await axios.post(ENDPOINT.CLOSE_PENDING_SALES, { userId, name }, token);

      setCashierSalesCount(itemsClosedCashier.length);
      setItemsClosedCashier([]);
      setCashierData([]);
      setIsLoading(false);
      closeModalSale();

      if (itemsClosedCashier.length > 0) {
        setShowSupplyModal(true);
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  const toggleSupply = (supply) => {
    setSelectedSupplies((prev) => ({
      ...prev,
      [supply]: !prev[supply],
    }));
  };

  const getSelectedSupplyList = () => {
    return Object.entries(selectedSupplies)
      .filter(([, checked]) => checked)
      .map(([supply]) => supply);
  };

  const closeSupplyModal = () => {
    setShowSupplyModal(false);
    setReportError(null);
    setSelectedSupplies({});
    setSendingReport(false);
  };

  const sendReport = async () => {
    const selectedList = getSelectedSupplyList();

    setSendingReport(true);
    setReportError(null);

    const token = getToken();
    const { userId } = getUserData();

    const formattedPaymentMethods = cashierPaymentMethods.map(
      (pm) => `${pm.paymentMethodName}: $${formatCurrency(pm.amount)}`
    );

    const formattedDeliveryPaymentMethods = cashierPaymentMethods.map(
      (pm) => `${pm.paymentMethodName}: $${formatCurrency(pm.deliveryAmount)}`
    );

    const payload = {
      totalAmount: cashierTotalAmount,
      totalDeliveryAmount: cashierTotalDeliveryAmount,
      totalSales: cashierSalesCount,
      userId,
      paymentMethods: formattedPaymentMethods,
      deliveryPaymentMethods: formattedDeliveryPaymentMethods,
      selectedSupplies: selectedList,
      comment: comment,
    };

    const MAX_RETRIES = 3;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        await axios.post(ENDPOINT.SEND_CASHIER_TELEGRAM, payload, token);
        setShowSupplyModal(false);
        setShowToast(true);
        setSendingReport(false);
        return;
      } catch (error) {
        console.error(`Intento ${attempt + 1} fallido:`, error);
        if (attempt === MAX_RETRIES - 1) {
          setSendingReport(false);
          setReportError(
            `No se pudo enviar el reporte. Envia los insumos manualmente:\n${selectedList.join(", ")}`
          );
        }
      }
    }
  };

  const getSendButtonContent = () => {
    if (sendingReport) {
      return (
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
      );
    }
    return "Enviar";
  };

  const getButtonContent = () => {
    if (isLoading) {
      return (
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
      );
    } else {
      return "Si";
    }
  };

  const columnClosedCashier = [
    {
      name: "Fecha",
      key: "date",
      type: "date",
    },
    {
      name: "Nombre Cliente",
      key: "clientName",
      type: "text",
    },
    {
      name: "Total",
      key: "totalPrice",
      type: "currency",
    },
    {
      name: "Domicilio",
      key: "deliveryPrice",
      type: "currency",
    },
    {
      name: "Metodo Pago",
      key: "payments",
      type: "arrayJoin",
      separator: ", ",
      property: "paymentMethodName",
    },
    ...(isAdmin
      ? [
          {
            name: "",
            type: "actions",
            buttons: [
              {
                iconClass: "fa-solid fa-trash-can",
                action: cancelSaleClick,
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <div className="row mx-4">
      <div className="row">
        <h1 className="style-title-page mt-4"> CIERRE DE CAJA </h1>
      </div>
      <div className="row justify-content-between">
        <div className="col-md-8 col-12 order-1 order-md-0">
          <div className="mt-5">
            <Table
              columnNames={columnClosedCashier}
              items={itemsClosedCashier}
              fixSize={"t-responsive-medium"}
              isLoading={isLoadingSales}
            />
          </div>
        </div>
        <div className="col-md-3 col-12 mt-md-5 mt-4">
          {cashierData &&
            cashierData.map((card, i) => (
              <CloseCashierCard key={i} list={card.data} title={card.title} />
            ))}
          <button
            className="btn-primary btn style-buttom-closed mt-4"
            onClick={closeSale}
          >
            {" "}
            CERRAR CAJA{" "}
          </button>
        </div>
      </div>

      <Modal show={closeSales} onHide={closeModalSale}>
        <ModalHeader closeButton className="style-modal-header">
          <ModalTitle className="text-modal"> CIERRE DE CAJA</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="text-center">
            <p> ¿Estas seguro que desea realizar cierre de caja? </p>
            <p className="yellow-color">
              {" "}
              (Recuerda revisar que tengas el mismo efectivo y
              transferencias){" "}
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="d-flex w-100 justify-content-center">
            <button
              className="btn btn-primary me-4 button-closed"
              onClick={closeCashier}
            >
              {" "}
              {getButtonContent()}{" "}
            </button>
            <button
              className="btn btn-primary button-closed"
              onClick={closeModalSale}
            >
              {" "}
              No{" "}
            </button>
          </div>
        </ModalFooter>
      </Modal>

      <Modal show={cancelSaleModal} onHide={closeCancelSale}>
        <ModalHeader closeButton className="style-modal-header">
          <ModalTitle className="text-modal"> CANCELAR PEDIDO </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="text-center">
            <p> ¿Estas seguro que deseas cancelar este pedido? </p>
            <p className="yellow-color">
              {" "}
              (El pedido volvera a la caja como no pago){" "}
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="d-flex w-100 justify-content-center">
            <button
              className="btn btn-primary me-4 button-closed"
              onClick={confirmCancelSale}
              disabled={cancelLoading}
            >
              {" "}
              {cancelButtonContent()}{" "}
            </button>
            <button
              className="btn btn-primary button-closed"
              onClick={closeCancelSale}
            >
              {" "}
              No{" "}
            </button>
          </div>
          {cancelErrorMsg && (
            <div className="row mt-4">
              <p className="error-msg">{cancelErrorMsg}</p>
            </div>
          )}
        </ModalFooter>
      </Modal>

      <Modal
        show={showSupplyModal}
        onHide={closeSupplyModal}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader className="style-modal-header">
          <ModalTitle className="text-modal">INSUMOS PARA COMPRAR</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {reportError ? (
            <div className="text-center">
              <p className="yellow-color">{reportError}</p>
              <button
                className="btn btn-primary button-closed mt-3"
                onClick={closeSupplyModal}
              >
                Cerrar
              </button>
            </div>
          ) : (
            <div>
              <p>Selecciona los insumos que hay que comprar:</p>
              <div className="row">
                <div className="col-md-6 col-12">
                  {SUPPLY_CHECKLIST.slice(
                    0,
                    Math.ceil(SUPPLY_CHECKLIST.length / 2)
                  ).map((supply) => (
                    <Form.Check
                      key={supply}
                      type="checkbox"
                      id={`supply-${supply}`}
                      label={supply}
                      checked={selectedSupplies[supply] || false}
                      onChange={() => toggleSupply(supply)}
                      className="mb-2"
                    />
                  ))}
                </div>
                <div className="col-md-6 col-12">
                  {SUPPLY_CHECKLIST.slice(
                    Math.ceil(SUPPLY_CHECKLIST.length / 2)
                  ).map((supply) => (
                    <Form.Check
                      key={supply}
                      type="checkbox"
                      id={`supply-${supply}`}
                      label={supply}
                      checked={selectedSupplies[supply] || false}
                      onChange={() => toggleSupply(supply)}
                      className="mb-2"
                    />
                  ))}
                </div>
              </div>
              <Form.Group className="mt-3">
                <Form.Label>Comentarios (opcional):</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Deja un mensaje adicional..."
                />
              </Form.Group>
            </div>
          )}
        </ModalBody>
        {!reportError && (
          <ModalFooter>
            <div className="d-flex w-100 justify-content-center">
              <button
                className="btn btn-primary button-closed"
                onClick={sendReport}
                disabled={sendingReport}
              >
                {getSendButtonContent()}
              </button>
            </div>
          </ModalFooter>
        )}
      </Modal>

      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={4000}
          autohide
          bg="success"
        >
          <Toast.Header>
            <strong className="me-auto">Que Bendición</strong>
          </Toast.Header>
          <Toast.Body className="text-white">
            Reporte enviado correctamente.
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default ClosedCashier;
